/** Ported from chief's `web/src/router.tsx` (MIT) — see "Licence and attribution" in README.md. */

import { type AnchorHTMLAttributes, type MouseEvent, useSyncExternalStore } from 'react';

/**
 * A history-API router small enough to read in one sitting.
 *
 * The app has a handful of entry points (Brand books at `/brand-books` the
 * newest) and two parameterised routes (a content, a brand book). What it needs
 * from a router is exactly three things — the current path as React state, a
 * `navigate()` that does not reload the page, and an `<a>` that calls it — and
 * nothing a library adds on top (nested outlets, loaders, data APIs) would be
 * used. The server serves `index.html` for every non-`/api` path, so any URL
 * here survives a reload.
 */

interface Location {
  readonly pathname: string;
  readonly search: string;
}

const listeners = new Set<() => void>();
let current: Location = read();

function read(): Location {
  return { pathname: window.location.pathname, search: window.location.search };
}

function emit(): void {
  current = read();
  for (const listener of listeners) listener();
}

window.addEventListener('popstate', emit);

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  const from = window.location.pathname;
  if (options.replace === true) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  emit();
  // The history API fires no `hashchange`, so a link to `#<id>` on the page
  // already open says so itself; a page it mounts reads the hash on mount.
  if (window.location.hash !== '' && window.location.pathname === from) {
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }
}

/** Replaces the query string without adding a history entry. */
export function replaceSearch(params: URLSearchParams): void {
  const query = params.toString();
  navigate(`${window.location.pathname}${query === '' ? '' : `?${query}`}`, { replace: true });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useLocation(): Location {
  return useSyncExternalStore(subscribe, () => current);
}

/**
 * Same-origin, unmodified left clicks become client-side navigations; anything
 * else (middle click, Ctrl/Cmd, `target="_blank"`, external hrefs) is left to
 * the browser so links keep every behaviour a link is expected to have.
 */
export function Link({
  href,
  onClick,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { readonly href: string }) {
  const handle = (event: MouseEvent<HTMLAnchorElement>): void => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (rest.target !== undefined && rest.target !== '_self') return;
    if (/^[a-z]+:/i.test(href)) return;
    event.preventDefault();
    navigate(href);
  };
  return <a href={href} onClick={handle} {...rest} />;
}

/** `/brand-books/<id>` → the id, or null for any other path. */
export function brandBookIdFromPath(pathname: string): string | null {
  const match = /^\/brand-books\/([^/]+)\/?$/.exec(pathname);
  return match === null ? null : decodeURIComponent(match[1] ?? '');
}

/** The brand book page (US-012). */
export function brandBookPath(id: string): string {
  return `/brand-books/${encodeURIComponent(id)}`;
}

/** `/content/<id>` → the id, or null for any other path. */
export function contentIdFromPath(pathname: string): string | null {
  const match = /^\/content\/([^/]+)\/?$/.exec(pathname);
  if (match === null || match[1] === 'new') return null;
  return decodeURIComponent(match[1] ?? '');
}

/** `/ideas/<id>` and `/ideas/<id>/review` → the batch id, or null for any other path. */
export function ideaBatchIdFromPath(pathname: string): string | null {
  const match = /^\/ideas\/([^/]+)(?:\/review)?\/?$/.exec(pathname);
  if (match === null || match[1] === 'new') return null;
  return decodeURIComponent(match[1] ?? '');
}

/** `/ads/ad/<meta id>` → the meta id, or null for any other path. */
export function adMetaIdFromPath(pathname: string): string | null {
  const match = /^\/ads\/ad\/([^/]+)\/?$/.exec(pathname);
  return match === null ? null : decodeURIComponent(match[1] ?? '');
}

/** One ad's detail view (US-022). */
export function adPath(metaId: string): string {
  return `/ads/ad/${encodeURIComponent(metaId)}`;
}

/** The Ads page's decision log, filtered on one campaign, ad set or ad (US-016). */
export function adsDecisionsPath(targetMetaId: string): string {
  return `/ads?${new URLSearchParams({ tab: 'decisions', target: targetMetaId }).toString()}`;
}

/** The Ads page's Reviews tab (US-017); with an id, that one review's detail. */
export function adsReviewsPath(runId?: string): string {
  const params = new URLSearchParams({ tab: 'reviews' });
  if (runId !== undefined) params.set('run', runId);
  return `/ads?${params.toString()}`;
}

/** The New Content page pre-filled from an ads creative request (US-019). */
export function newContentFromRequestPath(requestId: string): string {
  return `/content/new?${new URLSearchParams({ creativeRequest: requestId }).toString()}`;
}
